import React, { useState, useRef, useEffect } from "react";
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { useDispatch } from 'react-redux';

import api, { setToken } from '../../../../api/api';
import history from "../../../../history";
import { userLogin } from '../../../actions';

import EditProfileForm from '../../../Home/Forms/EditProfileForm';
import ChangePasswordForm from '../../../Home/Forms/ChangePasswordForm';
import ChangeLoginInfoForm from '../../../Home/Forms/ChangeLoginInfoForm';
import UserBill from '../../Users/UserBill';
import UserNote from '../../Users/UserNote';
import countriesData from "../../../Config/countries.json";

const getCountryName = (code) => {
    if (!code) return "";
    const cleanCode = code.toLowerCase();
    const found = countriesData.countries.find(c => c.value === cleanCode || c.alpha3?.toLowerCase() === cleanCode);
    return found ? found.label : code.toUpperCase();
};

const SMSModals = ({
    showBillsModal,
    setShowBillsModal,
    showNewChatModal,
    setShowNewChatModal,
    showUserInfoModal,
    setShowUserInfoModal,
    userInfoData,
    setUserInfoData,
    loadingUserInfo,
    bills,
    loadingBills,
    fetchBills,
    sendSMSMessage,
    getCountryFlag,
    t
}) => {
    const dispatch = useDispatch();

    // New SMS chat local state
    const [newPhone, setNewPhone] = useState("");
    const [newCountry, setNewCountry] = useState("il");
    const [newMessage, setNewMessage] = useState("");
    const [sendingNew, setSendingNew] = useState(false);

    const newCountryOptions = React.useMemo(() => {
        return countriesData.countries
            .filter(c => c.phoneCode && c.value)
            .sort((a, b) => a.label > b.label ? 1 : -1)
            .map(c => ({
                value: c.value.toLowerCase(),
                phoneCode: c.phoneCode,
                name: c.label,
                label: (
                    <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                        <img 
                            src={process.env.PUBLIC_URL + c.icon} 
                            alt={c.label} 
                            style={{ width: '20px', height: '14px', objectFit: 'cover', borderRadius: '2px' }}
                        />
                        <span>{c.label} (+{c.phoneCode})</span>
                    </div>
                )
            }));
    }, []);

    // Secondary Modal states for UserInfo management options (WhatsAppModals mirror)
    const [showEditProfile, setShowEditProfile] = useState(false);
    const [showBills, setShowBills] = useState(false);
    const [showNotes, setShowNotes] = useState(false);
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [showLoginInfo, setShowLoginInfo] = useState(false);
    const [showChangeSchool, setShowChangeSchool] = useState(false);
    const [showMove, setShowMove] = useState(false);

    const [schoolModalPurpose, setSchoolModalPurpose] = useState("school"); // "school" or "teacherType"

    const schoolSelectRef = useRef(null);
    const classRoomSelectRef = useRef(null);

    // Fetch ClickSend bills when Bills Modal opens
    useEffect(() => {
        if (showBillsModal) {
            fetchBills();
        }
    }, [showBillsModal, fetchBills]);

    // Format billing transaction dates
    const formatBillingDate = (ts) => {
        if (!ts) return "";
        const date = new Date(ts * 1000);
        return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };



    // Handle New SMS Submission
    const handleNewSMSSubmit = async (e) => {
        e.preventDefault();
        if (!newPhone.trim() || !newMessage.trim() || sendingNew) return;

        setSendingNew(true);
        // Prepend dial code based on country code
        const selectedCountry = countriesData.countries.find(c => c.value === newCountry.toLowerCase());
        const dialCode = selectedCountry ? selectedCountry.phoneCode : "";
        
        let cleanedPhone = newPhone.replace(/\D/g, '');
        // strip leading zero if present and dialCode is added
        if (dialCode && cleanedPhone.startsWith('0')) {
            cleanedPhone = cleanedPhone.substring(1);
        }
        
        const finalPhone = dialCode ? `${dialCode}${cleanedPhone}` : cleanedPhone;

        const success = await sendSMSMessage(finalPhone, newMessage);
        setSendingNew(false);

        if (success) {
            setShowNewChatModal(false);
            setNewPhone("");
            setNewMessage("");
            if (history) {
                history.push(`/ControlPanel/SMS/${finalPhone}`);
            }
        }
    };

    // API Helpers for users control
    const filterOptions = (options, inputValue) => {
        const candidate = (inputValue || "").toLowerCase();
        if (options.length > 0) {
            return options.filter(({ label }) => label.toLowerCase().includes(candidate));
        }
        return true;
    };

    const promiseSchoolOptions = (inputValue, callback) => {
        api.post(`/SchoolsLookups/`, {
            Name: inputValue
        }).then(resp => {
            if (resp.data.data?.length > 0) {
                const asyncOptions = resp.data.data.map(({ Id, Name }) => ({ label: Name, value: Id }));
                const filtered = filterOptions(asyncOptions, inputValue);
                callback(filtered);
            } else {
                callback();
            }
        });
    };

    const promiseClassRoomOptions = (inputValue, callback) => {
        if (!userInfoData || !userInfoData.schoolId) {
            callback();
            return;
        }
        api.post(`/ClassRoomsLookups/`, {
            Name: inputValue,
            SchoolId: userInfoData.schoolId
        }).then(resp => {
            if (resp.data.data?.length > 0) {
                const asyncOptions = resp.data.data.map(p => ({ label: p.ClassType.Name + " " + p.Division.Name, value: p.Id }));
                const filtered = filterOptions(asyncOptions, inputValue);
                callback(filtered);
            } else {
                callback();
            }
        });
    };

    // User Operations Submissions
    const onSubmitEditProfile = (postData, _toastId) => {
        api.put(`/Users/${userInfoData.id}`, postData)
            .then(response => {
                if (response.data.code === "success") {
                    toast.update(_toastId, {
                        render: () => <div><div className="h5"> <i className="icon fal fa-shield-check"></i> {t('Pages.ProfileEdit.success_toast.Title')}</div> <div className="h6">{t('Pages.ProfileEdit.success_toast.Body')}</div></div>,
                        type: toast.TYPE.SUCCESS,
                        position: "bottom-left",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: false,
                        pauseOnHover: true,
                        draggable: true,
                    });
                    setShowEditProfile(false);
                    refreshUserInfo();
                } else if (response.data.code === "exists_email") {
                    toast.update(_toastId, {
                        render: () => <div><div className="h5"> <i className="icon fal fa-bug"></i> {t('Pages.Register.Email.error_toast.Title')}</div> <div className="h6">{t('Pages.Register.Email.error_toast.Body')}</div></div>,
                        type: toast.TYPE.ERROR,
                        position: "bottom-left",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: false,
                        pauseOnHover: true,
                        draggable: true,
                    });
                } else {
                    toast.update(_toastId, {
                        render: () => <div><div className="h5"> <i className="icon fal fa-bug"></i> {t('Pages.ProfileEdit.error_AllInformationIsRequired_toast.Title')}</div> <div className="h6">{t('Pages.ProfileEdit.error_AllInformationIsRequired_toast.Body')}</div></div>,
                        type: toast.TYPE.ERROR,
                        position: "bottom-left",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: false,
                        pauseOnHover: true,
                        draggable: true,
                    });
                }
            })
            .catch(() => {
                toast.update(_toastId, {
                    render: () => <div><div className="h5"> <i className="icon fal fa-wifi-slash"></i> {t('Error.NoConnection.Title')}</div> <div className="h6">{t('Error.NoConnection.Body')}</div></div>,
                    type: toast.TYPE.ERROR,
                    position: "bottom-left",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: false,
                    pauseOnHover: true,
                    draggable: true,
                });
            });
    };

    const onSubmitChangePassword = (postData, _toastId) => {
        api.put(`/Users/${userInfoData.id}?ChangePassword=true`, postData)
            .then(response => {
                if (response.data.code === "success") {
                    toast.update(_toastId, {
                        render: () => <div><div className="h5"> <i className="icon fal fa-shield-check"></i> {t('Pages.ProfileEdit.success_toast.Title')}</div> <div className="h6">{t('Pages.ProfileEdit.success_toast.Body')}</div></div>,
                        type: toast.TYPE.SUCCESS,
                        position: "bottom-left",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: false,
                        pauseOnHover: true,
                        draggable: true,
                    });
                    setShowChangePassword(false);
                } else {
                    toast.update(_toastId, {
                        render: () => <div><div className="h5"> <i className="icon fal fa-bug"></i> {t('Pages.ProfileEdit.error_AllInformationIsRequired_toast.Title')}</div> <div className="h6">{t('Pages.ProfileEdit.error_AllInformationIsRequired_toast.Body')}</div></div>,
                        type: toast.TYPE.ERROR,
                        position: "bottom-left",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: false,
                        pauseOnHover: true,
                        draggable: true,
                    });
                }
            })
            .catch(() => {
                toast.update(_toastId, {
                    render: () => <div><div className="h5"> <i className="icon fal fa-wifi-slash"></i> {t('Error.NoConnection.Title')}</div> <div className="h6">{t('Error.NoConnection.Body')}</div></div>,
                    type: toast.TYPE.ERROR,
                    position: "bottom-left",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: false,
                    pauseOnHover: true,
                    draggable: true,
                });
            });
    };

    const onSubmitLoginInfo = (postData) => {
        const _toastId = toast(t('Processing'), { position: "bottom-left", autoClose: false });
        api.post(`/ChangeUserLoginInfo/${userInfoData.id}`, postData)
            .then(response => {
                if (response.data.code === "success") {
                    toast.update(_toastId, {
                        render: () => <div><div className="h5"> <i className="icon fal fa-shield-check"></i> {t('Pages.ProfileEdit.success_toast.Title')}</div> <div className="h6">{t('Pages.ProfileEdit.success_toast.Body')}</div></div>,
                        type: toast.TYPE.SUCCESS,
                        position: "bottom-left",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: false,
                        pauseOnHover: true,
                        draggable: true,
                    });
                    setShowLoginInfo(false);
                    refreshUserInfo();
                } else if (response.data.code === "exists_username") {
                    toast.update(_toastId, {
                        render: () => <div><div className="h5"> <i className="icon fal fa-bug"></i> {t('Pages.Register.Username.error_toast.Title')}</div> <div className="h6">{t('Pages.Register.Username.error_toast.Body')}</div></div>,
                        type: toast.TYPE.ERROR,
                        position: "bottom-left",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: false,
                        pauseOnHover: true,
                        draggable: true,
                    });
                } else if (response.data.code === "exists_email") {
                    toast.update(_toastId, {
                        render: () => <div><div className="h5"> <i className="icon fal fa-bug"></i> {t('Pages.Register.Email.error_toast.Title')}</div> <div className="h6">{t('Pages.Register.Email.error_toast.Body')}</div></div>,
                        type: toast.TYPE.ERROR,
                        position: "bottom-left",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: false,
                        pauseOnHover: true,
                        draggable: true,
                    });
                } else {
                    toast.update(_toastId, {
                        render: () => <div><div className="h5"> <i className="icon fal fa-bug"></i> {t('Pages.ProfileEdit.error_AllInformationIsRequired_toast.Title')}</div> <div className="h6">{t('Pages.ProfileEdit.error_AllInformationIsRequired_toast.Body')}</div></div>,
                        type: toast.TYPE.ERROR,
                        position: "bottom-left",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: false,
                        pauseOnHover: true,
                        draggable: true,
                    });
                }
            })
            .catch(() => {
                toast.update(_toastId, {
                    render: () => <div><div className="h5"> <i className="icon fal fa-wifi-slash"></i> {t('Error.NoConnection.Title')}</div> <div className="h6">{t('Error.NoConnection.Body')}</div></div>,
                    type: toast.TYPE.ERROR,
                    position: "bottom-left",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: false,
                    pauseOnHover: true,
                    draggable: true,
                });
            });
    };

    const handleSchoolSave = () => {
        const selectedValue = schoolSelectRef.current?.select?.select?.getValue()?.[0];
        if (!selectedValue) {
            toast.error(t("Pages.UsersControl.toast.ChangeSchool.error"));
            return;
        }

        const _toastId = toast(t('Processing'), { position: "bottom-left", autoClose: false });

        if (schoolModalPurpose === "teacherType") {
            const postData = {
                SchoolId: selectedValue.value,
                UserTypeId: 4
            };
            api.put(`/ChangeUserType/${userInfoData.id}`, postData)
                .then(() => {
                    toast.update(_toastId, {
                        render: () => <div><div className="h5"> <i className="icon fal fa-shield-check"></i> {t('Pages.UsersControl.toast.ChangePermission.Title')}</div> <div className="h6">{t('Pages.UsersControl.toast.ChangePermission.Body')}</div></div>,
                        type: toast.TYPE.INFO,
                        position: "bottom-left",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: false,
                        pauseOnHover: true,
                        draggable: true,
                    });
                    setShowChangeSchool(false);
                    refreshUserInfo();
                })
                .catch(() => {
                    toast.update(_toastId, {
                        render: () => <div><div className="h5"> <i className="icon fal fa-wifi-slash"></i> {t('Error.NoConnection.Title')}</div> <div className="h6">{t('Error.NoConnection.Body')}</div></div>,
                        type: toast.TYPE.ERROR,
                        position: "bottom-left",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: false,
                        pauseOnHover: true,
                        draggable: true,
                    });
                });
        } else {
            api.post(`/ChangeUserSchool/${userInfoData.id}`, { SchoolId: selectedValue.value })
                .then(() => {
                    toast.update(_toastId, {
                        render: () => <div><div className="h5"> <i className="icon fal fa-school"></i> {t('Pages.UsersControl.toast.ChangeSchool.Title')}</div> <div className="h6">{t('Pages.UsersControl.toast.ChangeSchool.Body')}</div></div>,
                        type: toast.TYPE.SUCCESS,
                        position: "bottom-left",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: false,
                        pauseOnHover: true,
                        draggable: true,
                    });
                    setShowChangeSchool(false);
                    refreshUserInfo();
                })
                .catch(() => {
                    toast.update(_toastId, {
                        render: () => <div><div className="h5"> <i className="icon fal fa-wifi-slash"></i> {t('Error.NoConnection.Title')}</div> <div className="h6">{t('Error.NoConnection.Body')}</div></div>,
                        type: toast.TYPE.ERROR,
                        position: "bottom-left",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: false,
                        pauseOnHover: true,
                        draggable: true,
                    });
                });
        }
    };

    const handleClassRoomSave = () => {
        const selectedValue = classRoomSelectRef.current?.select?.select?.getValue()?.[0];
        if (!selectedValue) {
            toast.error(t("Pages.UsersControl.toast.ChangeClassRoom.error"));
            return;
        }

        const _toastId = toast(t('Processing'), { position: "bottom-left", autoClose: false });
        api.post(`/ChangeStudentClass/${userInfoData.id}`, { ClassRoomId: selectedValue.value })
            .then(() => {
                toast.update(_toastId, {
                    render: () => <div><div className="h5"><i className="icon fal fa-exchange"></i>{t('Pages.UsersControl.toast.ChangeClassRoom.Title')}</div><div className="h6">{t('Pages.UsersControl.toast.ChangeClassRoom.Body')}</div></div>,
                    type: toast.TYPE.SUCCESS,
                    position: "bottom-left",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: false,
                    pauseOnHover: true,
                    draggable: true,
                });
                setShowMove(false);
                refreshUserInfo();
            })
            .catch(() => {
                toast.update(_toastId, {
                    render: () => <div><div className="h5"> <i className="icon fal fa-wifi-slash"></i> {t('Error.NoConnection.Title')}</div> <div className="h6">{t('Error.NoConnection.Body')}</div></div>,
                    type: toast.TYPE.ERROR,
                    position: "bottom-left",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: false,
                    pauseOnHover: true,
                    draggable: true,
                });
            });
    };

    // User Operations Toggle triggers
    const changeUserType = () => {
        if (userInfoData.userTypeId === 4) {
            if (window.confirm(t("ChangePermissionUserProcessing"))) {
                const _toastId = toast(t('Processing'), { position: "bottom-left", autoClose: false });
                api.put(`/ChangeUserType/${userInfoData.id}`, { SchoolId: null, UserTypeId: 2 })
                    .then(() => {
                        toast.update(_toastId, {
                            render: () => <div><div className="h5"> <i className="icon fal fa-shield-check"></i> {t('Pages.UsersControl.toast.ChangePermission.Title')}</div> <div className="h6">{t('Pages.UsersControl.toast.ChangePermission.Body')}</div></div>,
                            type: toast.TYPE.INFO,
                            position: "bottom-left",
                            autoClose: 5000,
                            hideProgressBar: false,
                            closeOnClick: false,
                            pauseOnHover: true,
                            draggable: true,
                        });
                        refreshUserInfo();
                    })
                    .catch(() => {
                        toast.update(_toastId, { type: toast.TYPE.ERROR, autoClose: 5000, render: () => <div>{t('Error.NoConnection.Title')}</div> });
                    });
            }
        } else if (userInfoData.userTypeId === 2) {
            setSchoolModalPurpose("teacherType");
            setShowChangeSchool(true);
        }
    };

    const changePermission = () => {
        if (window.confirm(t("ChangePermissionUserProcessing"))) {
            const _toastId = toast(t('Processing'), { position: "bottom-left", autoClose: false });
            const targetType = userInfoData.userTypeId === 3 ? 4 : 3;
            api.post(`/ChangeUserType/${userInfoData.id}`, { UserTypeId: targetType })
                .then(() => {
                    toast.update(_toastId, {
                        render: () => <div><div className="h5"> <i className="icon fal fa-shield-check"></i> {t('Pages.UsersControl.toast.ChangePermission.Title')}</div> <div className="h6">{t('Pages.UsersControl.toast.ChangePermission.Body')}</div></div>,
                        type: toast.TYPE.INFO,
                        position: "bottom-left",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: false,
                        pauseOnHover: true,
                        draggable: true,
                    });
                    refreshUserInfo();
                })
                .catch(() => {
                    toast.update(_toastId, { type: toast.TYPE.ERROR, autoClose: 5000, render: () => <div>{t('Error.NoConnection.Title')}</div> });
                });
        }
    };

    const changeAccessToAllClass = () => {
        if (window.confirm(t("ChangeAccessToClassProcessing"))) {
            const _toastId = toast(t('Processing'), { position: "bottom-left", autoClose: false });
            api.post(`/ChangeAccessToAllClass/${userInfoData.id}`)
                .then(() => {
                    toast.update(_toastId, {
                        render: () => <div><div className="h5"> <i className="icon fal fa-shield-check"></i> {t('Pages.UsersControl.toast.ChangePermission.Title')}</div> <div className="h6">{t('Pages.UsersControl.toast.ChangePermission.Body')}</div></div>,
                        type: toast.TYPE.INFO,
                        position: "bottom-left",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: false,
                        pauseOnHover: true,
                        draggable: true,
                    });
                    refreshUserInfo();
                })
                .catch(() => {
                    toast.update(_toastId, { type: toast.TYPE.ERROR, autoClose: 5000, render: () => <div>{t('Error.NoConnection.Title')}</div> });
                });
        }
    };

    const toggleTestUser = () => {
        if (window.confirm(t("TestUserProcessing"))) {
            const _toastId = toast(t('Processing'), { position: "bottom-left", autoClose: false });
            api.put(`/UserAsUser/${userInfoData.id}`)
                .then(() => {
                    toast.update(_toastId, {
                        render: () => <div><div className="h5"> <i className="icon fal fa-shield-check"></i> {t('Pages.UsersControl.toast.test_user.Title')}</div> <div className="h6">{t('Pages.UsersControl.toast.test_user.Body')}</div></div>,
                        type: toast.TYPE.INFO,
                        position: "bottom-left",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: false,
                        pauseOnHover: true,
                        draggable: true,
                    });
                    refreshUserInfo();
                })
                .catch(() => {
                    toast.update(_toastId, { type: toast.TYPE.ERROR, autoClose: 5000, render: () => <div>{t('Error.NoConnection.Title')}</div> });
                });
        }
    };

    const loginAsUser = () => {
        if (window.confirm(t("UserAsProcessing"))) {
            const _toastId = toast(t('Processing'), { position: "bottom-left", autoClose: false });
            api.post(`/UserAsUser/${userInfoData.id}`)
                .then(response => {
                    if (response.data.code === "success") {
                        let prevToken = localStorage.getItem('token');
                        localStorage.setItem('prevToken', prevToken);
                        localStorage.setItem('token', response.data.token);
                        dispatch(userLogin(response.data));
                        setToken(response.data.token);

                        toast.update(_toastId, {
                            render: () => <div><div className="h5"> <i className="icon fal fa-shield-check"></i> {t('Pages.Login.success_toast.Title')}</div> <div className="h6">{t('Pages.Login.success_toast.Body')}</div></div>,
                            type: toast.TYPE.INFO,
                            position: "bottom-left",
                            autoClose: 5000,
                            hideProgressBar: false,
                            closeOnClick: false,
                            pauseOnHover: true,
                            draggable: true,
                        });
                        setShowUserInfoModal(false);
                        history.push("/Profile");
                    }
                })
                .catch(() => {
                    toast.update(_toastId, { type: toast.TYPE.ERROR, autoClose: 5000, render: () => <div>{t('Error.NoConnection.Title')}</div> });
                });
        }
    };

    const deleteUser = () => {
        if (window.confirm(t("DeleteProcessing"))) {
            const _toastId = toast(t('Processing'), { position: "bottom-left", autoClose: false });
            api.delete(`/Users/${userInfoData.id}`)
                .then(() => {
                    toast.update(_toastId, {
                        render: () => <div><div className="h5"> <i className="icon fal fa-exclamation-triangle"></i> {t('Pages.UsersControl.toast.Delete.Title')}</div> <div className="h6">{t('Pages.UsersControl.toast.Delete.Body')}</div></div>,
                        type: toast.TYPE.SUCCESS,
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: false,
                        pauseOnHover: true,
                        draggable: true,
                    });
                    setShowUserInfoModal(false);
                })
                .catch(() => {
                    toast.update(_toastId, { type: toast.TYPE.ERROR, autoClose: 5000, render: () => <div>{t('Error.NoConnection.Title')}</div> });
                });
        }
    };

    const refreshUserInfo = () => {
        if (!userInfoData || !userInfoData.phone) return;
        api.get(`/sms/user-info?phone=${userInfoData.phone}`)
            .then(response => {
                setUserInfoData(response.data);
            })
            .catch(() => {});
    };

    // User formatting mapping for compat views
    const compatUser = userInfoData && !userInfoData.isWhatsAppOnly ? {
        Id: userInfoData.id,
        Name: (userInfoData.firstName || '') + ' ' + (userInfoData.lastName || ''),
        FirstName: userInfoData.firstName,
        SecondName: '',
        LastName: userInfoData.lastName,
        Username: userInfoData.username,
        Email: userInfoData.email,
        Phone: userInfoData.phone,
        Country: userInfoData.country,
        Photo: userInfoData.photo,
        Birthday: userInfoData.birthday || '01/01/2000',
        Sex: userInfoData.sex || 'male',
        Address: userInfoData.address || '',
        IsLocked: userInfoData.isLocked,
        IsTeacher: userInfoData.isTeacher || false,
        MaxSubUser: userInfoData.maxSubUser || '',
        UserType: { Id: userInfoData.userTypeId, Name: userInfoData.userTypeName },
        Notes: userInfoData.notesCount || 0
    } : null;

    return (
        <>
            {/* CLICK-SEND BILLS MODAL */}
            <Modal show={showBillsModal} onHide={() => setShowBillsModal(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="fad fa-file-invoice-dollar text-success ml-2"></i>
                        {t("Pages.SMS.billsTitle") || "سجل شحن رصيد ClickSend"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loadingBills ? (
                        <div className="text-center py-5">
                            <i className="fa-spin fas fa-spinner text-success h3"></i>
                            <p className="mt-2 text-muted">{t("Loading")}</p>
                        </div>
                    ) : bills.length > 0 ? (
                        <div className="sms-bills-table-wrapper">
                            <table className="sms-bills-table">
                                <thead>
                                    <tr>
                                        <th>{t("Pages.SMS.transactionId") || "رقم العملية"}</th>
                                        <th>{t("Pages.SMS.amount") || "المبلغ"}</th>
                                        <th>{t("Pages.SMS.currency") || "العملة"}</th>
                                        <th>{t("Pages.SMS.paymentMethod") || "طريقة الدفع"}</th>
                                        <th>{t("Pages.SMS.date") || "التاريخ"}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...bills].sort((a, b) => b.date - a.date).map((b) => {
                                        const transactionId = b.invoice_number || b.transaction_id;
                                        return (
                                            <tr key={transactionId}>
                                                <td dir="ltr">{transactionId}</td>
                                                <td style={{ fontWeight: 'bold' }}>{b.amount}</td>
                                                <td>{b.currency || "USD"}</td>
                                                <td>{b.payment_method || "Credit Card"}</td>
                                                <td dir="ltr">{formatBillingDate(b.date)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-4 text-muted">
                            {t("NoInfo") || "لا توجد معلومات فواتير حالية."}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="sms-icon-btn" onClick={() => setShowBillsModal(false)}>
                        {t("Close")}
                    </button>
                </Modal.Footer>
            </Modal>

            {/* SEND SMS NEW MESSAGE COMPOSER MODAL */}
            <Modal show={showNewChatModal} onHide={() => setShowNewChatModal(false)} centered size="md">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="fad fa-comment-medical text-primary ml-2"></i>
                        {t("Pages.SMS.send") || "إرسال رسالة نصية جديدة"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={handleNewSMSSubmit}>
                        <div className="form-group">
                            <label className="font-weight-bold">{t("Register")}</label>
                            <div className="d-flex gap-2">
                                <Select
                                    value={newCountryOptions.find(opt => opt.value === newCountry.toLowerCase())}
                                    onChange={(val) => setNewCountry(val ? val.value : "il")}
                                    options={newCountryOptions}
                                    placeholder={t("Pages.Register.Country.Placeholder")}
                                    isClearable={false}
                                    isRtl={true}
                                    isSearchable={true}
                                    filterOption={(candidate, input) => {
                                        if (input) {
                                            const nameStr = (candidate.data.name || "").toLowerCase();
                                            const phoneStr = (candidate.data.phoneCode || "").toLowerCase();
                                            const inputLower = input.toLowerCase();
                                            return nameStr.includes(inputLower) || phoneStr.includes(inputLower);
                                        }
                                        return true;
                                    }}
                                    theme={theme => ({
                                        ...theme,
                                        borderRadius: 6,
                                        colors: {
                                            ...theme.colors,
                                            primary25: '#00a884',
                                            primary: '#00a884',
                                        },
                                    })}
                                    styles={{
                                        control: (provided) => ({
                                            ...provided,
                                            minHeight: '38px',
                                            width: '200px',
                                            fontSize: '14px',
                                            borderRadius: '6px',
                                            borderColor: '#ced4da',
                                            boxShadow: 'none',
                                            '&:hover': {
                                                borderColor: '#00a884'
                                            }
                                        }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            fontSize: '13px',
                                            padding: '6px 12px',
                                            backgroundColor: state.isSelected ? '#00a884' : (state.isFocused ? '#f1f5f9' : 'transparent'),
                                            color: state.isSelected ? '#ffffff' : '#334155',
                                            '&:active': {
                                                backgroundColor: '#00a884',
                                                color: '#ffffff'
                                            }
                                        })
                                    }}
                                />
                                <input
                                    type="text"
                                    className="form-control flex-grow-1"
                                    style={{ borderRadius: '6px' }}
                                    placeholder={t("Pages.SMS.phonePlaceholder") || "أدخل رقم الهاتف بدون رمز دولي"}
                                    value={newPhone}
                                    onChange={(e) => setNewPhone(e.target.value)}
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-group mt-3">
                            <label className="font-weight-bold">{t("Pages.SMS.typeMessage") || "نص الرسالة"}</label>
                            <textarea
                                className="form-control"
                                rows="4"
                                style={{ borderRadius: '6px', resize: 'none' }}
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                required
                            />
                        </div>

                        <div className="d-flex justify-content-end gap-2 mt-4">
                            <button type="button" className="sms-icon-btn" onClick={() => setShowNewChatModal(false)}>
                                {t("Close")}
                            </button>
                            <button type="submit" className="sms-send-btn" style={{ borderRadius: '6px', width: 'auto', padding: '0 20px' }} disabled={sendingNew}>
                                {sendingNew ? <i className="fa-spin fas fa-spinner"></i> : t("Pages.UsersControl.notes.Send")}
                            </button>
                        </div>
                    </form>
                </Modal.Body>
            </Modal>

            {/* USER INFO DETAILS AND MANAGEMENT MODAL */}
            <Modal show={showUserInfoModal} onHide={() => setShowUserInfoModal(false)} centered size="lg" className="text-right">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="fad fa-user-shield text-primary ml-2"></i>
                        {t("Pages.SMS.userInfo") || "بيانات مستخدم النظام"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loadingUserInfo ? (
                        <div className="text-center py-5">
                            <i className="fa-spin fas fa-spinner text-success h3"></i>
                            <p className="mt-2 text-muted">{t("Loading")}</p>
                        </div>
                    ) : userInfoData ? (
                        <div className="sms-user-info-body text-right">
                            {/* Avatars */}
                            <div className="sms-user-info-avatars">
                                {userInfoData.photo ? (
                                    <div className="text-center">
                                        <div className="sms-user-info-avatar">
                                            <img 
                                                src={
                                                    userInfoData.photo.startsWith("http")
                                                        ? userInfoData.photo
                                                        : (process.env.REACT_APP_BASE_URL || "") + (userInfoData.photo.startsWith("/") ? "" : "/") + userInfoData.photo
                                                } 
                                                alt={userInfoData.firstName} 
                                            />
                                        </div>
                                        <small className="text-muted d-block mt-2 font-weight-bold">{t("Pages.WhatsApp.dbPhoto") || "صورة النظام"}</small>
                                    </div>
                                ) : (
                                    <div className="sms-user-info-avatar">
                                        {String(userInfoData.firstName || "?").substring(0, 1).toUpperCase()}
                                    </div>
                                )}
                            </div>

                            {/* Details Grid */}
                            <div className="sms-user-info-details">
                                <div className="sms-user-info-row full-width">
                                    <span className="sms-user-info-label">{t("Pages.WhatsApp.dbStatus") || "حالة المشترك بالنظام"}</span>
                                    <span className="sms-user-info-val">
                                        {userInfoData.isWhatsAppOnly ? (
                                            <span className="sms-user-info-status locked">{t("Pages.WhatsApp.unregisteredUser") || "غير مسجل بالنظام"}</span>
                                        ) : (
                                            <span className="sms-user-info-status active">{t("Pages.WhatsApp.registeredUser") || "مسجل بالنظام"}</span>
                                        )}
                                    </span>
                                </div>
                                <div className="sms-user-info-row">
                                    <span className="sms-user-info-label">{t("Pages.WhatsApp.firstNamePlaceholder") || "الاسم"}</span>
                                    <span className="sms-user-info-val">{userInfoData.firstName} {userInfoData.lastName}</span>
                                </div>
                                <div className="sms-user-info-row">
                                    <span className="sms-user-info-label">{t("Pages.WhatsApp.phone") || "رقم الهاتف"}</span>
                                    <span className="sms-user-info-val" dir="ltr">{userInfoData.phone}</span>
                                </div>
                                {userInfoData.country && (
                                    <div className="sms-user-info-row">
                                        <span className="sms-user-info-label">{t("Pages.WhatsApp.country") || "الدولة"}</span>
                                        <span className="sms-user-info-val">
                                            {getCountryFlag(userInfoData.country) && (
                                                <img
                                                    src={process.env.PUBLIC_URL + getCountryFlag(userInfoData.country)}
                                                    style={{ width: "24px", height: "auto", display: "inline-block", marginLeft: "6px" }}
                                                    alt={userInfoData.country}
                                                />
                                            )}
                                            {getCountryName(userInfoData.country)}
                                        </span>
                                    </div>
                                )}
                                {!userInfoData.isWhatsAppOnly && (
                                    <>
                                        <div className="sms-user-info-row">
                                            <span className="sms-user-info-label">{t("Pages.WhatsApp.username") || "اسم المستخدم"}</span>
                                            <span className="sms-user-info-val">{userInfoData.username}</span>
                                        </div>
                                        <div className="sms-user-info-row full-width">
                                            <span className="sms-user-info-label">{t("Pages.WhatsApp.email") || "البريد الإلكتروني"}</span>
                                            <span className="sms-user-info-val">{userInfoData.email}</span>
                                        </div>
                                        {userInfoData.schoolName && (
                                            <div className="sms-user-info-row">
                                                <span className="sms-user-info-label">{t("Pages.WhatsApp.school") || "المدرسة"}</span>
                                                <span className="sms-user-info-val">{userInfoData.schoolName}</span>
                                            </div>
                                        )}
                                        <div className="sms-user-info-row">
                                            <span className="sms-user-info-label">{t("Pages.WhatsApp.userType") || "نوع الحساب"}</span>
                                            <span className="sms-user-info-val">{userInfoData.userTypeName}</span>
                                        </div>
                                        {userInfoData.birthday && (
                                            <div className="sms-user-info-row">
                                                <span className="sms-user-info-label">{t("Pages.Register.Birthday.title") || "تاريخ الميلاد"}</span>
                                                <span className="sms-user-info-val">{userInfoData.birthday}</span>
                                            </div>
                                        )}
                                        {userInfoData.creationDate && (
                                            <div className="sms-user-info-row">
                                                <span className="sms-user-info-label">{t("Pages.WhatsApp.creationDate") || "تاريخ التسجيل"}</span>
                                                <span className="sms-user-info-val">{userInfoData.creationDate}</span>
                                            </div>
                                        )}
                                        <div className="sms-user-info-row">
                                            <span className="sms-user-info-label">{t("Pages.WhatsApp.status") || "حالة الحساب"}</span>
                                            <span className="sms-user-info-val">
                                                {userInfoData.isLocked ? (
                                                    <span className="sms-user-info-status locked">{t("Pages.WhatsApp.locked") || "محظور"}</span>
                                                ) : (
                                                    <span className="sms-user-info-status active">{t("Pages.WhatsApp.active") || "نشط"}</span>
                                                )}
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Control action buttons for registered users */}
                            {!userInfoData.isWhatsAppOnly && (
                                <div className="sms-user-info-actions">
                                    {userInfoData.userTypeId !== 7 && (
                                        <button className="sms-action-btn" onClick={() => setShowEditProfile(true)} title={t("Pages.UsersControl.action.edit")}>
                                            <i className="far fa-edit"></i>
                                            <span>{t("Pages.UsersControl.action.edit")}</span>
                                        </button>
                                    )}

                                    {(userInfoData.userTypeId === 2 || userInfoData.userTypeId === 7) && (
                                        <button className="sms-action-btn" onClick={() => setShowBills(true)} title={t("Pages.UsersControl.action.bills")}>
                                            <i className="far fa-file-invoice-dollar"></i>
                                            <span>{t("Pages.UsersControl.action.bills")}</span>
                                        </button>
                                    )}

                                    <button className="sms-action-btn" onClick={() => setShowNotes(true)} style={{ position: 'relative' }} title={t("Pages.UsersControl.action.notes")}>
                                        <i className={`far ${userInfoData.notesCount > 0 ? "text-danger fa-notes" : "fa-sticky-note"}`}></i>
                                        <span>{t("Pages.UsersControl.action.notes")}</span>
                                        {userInfoData.notesCount > 0 && <span className="sms-notes-badge">{userInfoData.notesCount}</span>}
                                    </button>

                                    {userInfoData.userTypeId !== 7 && (
                                        <button className="sms-action-btn" onClick={() => setShowChangePassword(true)} title={t("Pages.UsersControl.action.change_password")}>
                                            <i className="far fa-key"></i>
                                            <span>{t("Pages.UsersControl.action.change_password")}</span>
                                        </button>
                                    )}

                                    {userInfoData.userTypeId !== 1 && userInfoData.userTypeId !== 7 && (
                                        <button className="sms-action-btn" onClick={() => setShowLoginInfo(true)} title={t("Pages.UsersControl.action.changeLoginInfo")}>
                                            <i className="far fa-fingerprint text-success"></i>
                                            <span>{t("Pages.UsersControl.action.changeLoginInfo")}</span>
                                        </button>
                                    )}

                                    {userInfoData.userTypeId === 4 && (
                                        <button className="sms-action-btn" onClick={() => { setSchoolModalPurpose("school"); setShowChangeSchool(true); }} title={t("Pages.UsersControl.action.changeSchool")}>
                                            <i className="far fa-school text-primary"></i>
                                            <span>{t("Pages.UsersControl.action.changeSchool")}</span>
                                        </button>
                                    )}

                                    {userInfoData.userTypeId === 2 && (
                                        <button className="sms-action-btn" onClick={changeUserType} title={t("Pages.UsersControl.action.changeTypeToSchoolTeacher")}>
                                            <i className="far fa-chalkboard-teacher text-warning"></i>
                                            <span>{t("Pages.UsersControl.action.changeTypeToSchoolTeacher")}</span>
                                        </button>
                                    )}
                                    {userInfoData.userTypeId === 4 && (
                                        <button className="sms-action-btn" onClick={changeUserType} title={t("Pages.UsersControl.action.changeTypeToUser")}>
                                            <i className="far fa-user-tag text-warning"></i>
                                            <span>{t("Pages.UsersControl.action.changeTypeToUser")}</span>
                                        </button>
                                    )}
                                    {userInfoData.userTypeId === 3 && (
                                        <button className="sms-action-btn" onClick={changePermission} title={t("Pages.UsersControl.action.makeSchoolTeacher")}>
                                            <i className="fad fa-water-lower text-danger"></i>
                                            <span>{t("Pages.UsersControl.action.makeSchoolTeacher")}</span>
                                        </button>
                                    )}
                                    {userInfoData.userTypeId === 4 && (
                                        <button className="sms-action-btn" onClick={changePermission} title={t("Pages.UsersControl.action.makeSchoolManager")}>
                                            <i className="fad fa-water-rise text-danger"></i>
                                            <span>{t("Pages.UsersControl.action.makeSchoolManager")}</span>
                                        </button>
                                    )}

                                    {(userInfoData.userTypeId === 3 || userInfoData.userTypeId === 4) && (
                                        <button className="sms-action-btn" onClick={changeAccessToAllClass} title={t("Pages.UsersControl.action.accessToAllClass")}>
                                            <i className="fa-solid fa-users-between-lines text-primary"></i>
                                            <span>{t("Pages.UsersControl.action.accessToAllClass")}</span>
                                        </button>
                                    )}

                                    {userInfoData.userTypeId === 5 && (
                                        <button className="sms-action-btn" onClick={() => setShowMove(true)} title={t("Pages.StudentInClassRoomsControl.action.move")}>
                                            <i className="far fa-exchange text-warning"></i>
                                            <span>{t("Pages.StudentInClassRoomsControl.action.move")}</span>
                                        </button>
                                    )}

                                    {userInfoData.userTypeId === 2 && (
                                        <button className="sms-action-btn" onClick={toggleTestUser} title={t("Pages.UsersControl.action.testUser")}>
                                            <i className="fad fa-alarm-exclamation text-danger"></i>
                                            <span>{t("Pages.UsersControl.action.testUser")}</span>
                                        </button>
                                    )}

                                    {userInfoData.userTypeId !== 7 && (
                                        <button className="sms-action-btn" onClick={loginAsUser} title={t("Pages.UsersControl.action.useAs") + userInfoData.firstName}>
                                            <i className="fad fa-alien text-info"></i>
                                            <span>{t("Pages.UsersControl.action.useAs")}</span>
                                        </button>
                                    )}

                                    {userInfoData.userTypeId !== 7 && (
                                        <button className="sms-action-btn" onClick={deleteUser} title={t("Pages.UsersControl.action.remove")}>
                                            <i className="far fa-trash text-danger"></i>
                                            <span>{t("Pages.UsersControl.action.remove")}</span>
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-4 text-muted">
                            {t("Pages.WhatsApp.userNotFound") || "لم يتم العثور على المشترك."}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="sms-icon-btn" onClick={() => setShowUserInfoModal(false)}>
                        {t("Close")}
                    </button>
                </Modal.Footer>
            </Modal>

            {/* SECONDARY OPERATIONS MODALS COMPAT */}
            {compatUser && (
                <>
                    {showBills && (
                        <UserBill handleClose={() => setShowBills(false)} user={compatUser} />
                    )}

                    {showNotes && (
                        <UserNote handleClose={() => { setShowNotes(false); refreshUserInfo(); }} user={compatUser} />
                    )}

                    {showEditProfile && (
                        <Modal size="lg" centered backdrop="static" show={showEditProfile} onHide={() => setShowEditProfile(false)}>
                            <Modal.Header closeButton>
                                <Modal.Title>
                                    <div className="h5 mb-0">
                                        <i className="fad fa-edit text-primary icon"></i> {t("Pages.UsersControl.action.edit")}:
                                        <span className="text-primary mx-1">{compatUser.Name}</span>
                                    </div>
                                </Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <div className="widget widget-category">
                                    <div className="widget-body">
                                        <EditProfileForm isAdmin={true} user={compatUser} onSubmit={onSubmitEditProfile} />
                                    </div>
                                </div>
                            </Modal.Body>
                        </Modal>
                    )}

                    {showChangePassword && (
                        <Modal size="md" centered backdrop="static" show={showChangePassword} onHide={() => setShowChangePassword(false)}>
                            <Modal.Header closeButton>
                                <Modal.Title>
                                    <div className="h5 mb-0">
                                        <i className="fad fa-key text-primary icon"></i> {t("Pages.UsersControl.action.change_password")}:
                                        <span className="text-primary mx-1">{compatUser.Name}</span>
                                    </div>
                                </Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <div className="widget widget-category">
                                    <div className="widget-body">
                                        <ChangePasswordForm onSubmit={onSubmitChangePassword} />
                                    </div>
                                </div>
                            </Modal.Body>
                        </Modal>
                    )}

                    {showLoginInfo && (
                        <Modal size="md" centered backdrop="static" show={showLoginInfo} onHide={() => setShowLoginInfo(false)}>
                            <Modal.Header closeButton>
                                <Modal.Title>
                                    <div className="h5 mb-0">
                                        <i className="fad fa-fingerprint text-success icon"></i> {t("Pages.UsersControl.action.changeLoginInfo")}:
                                        <span className="text-primary mx-1">{compatUser.Name}</span>
                                    </div>
                                </Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <div className="widget widget-category">
                                    <div className="widget-body">
                                        <ChangeLoginInfoForm onSubmit={onSubmitLoginInfo} user={compatUser} />
                                    </div>
                                </div>
                            </Modal.Body>
                        </Modal>
                    )}

                    {showChangeSchool && (
                        <Modal size="md" centered backdrop="static" show={showChangeSchool} onHide={() => setShowChangeSchool(false)}>
                            <Modal.Header closeButton>
                                <Modal.Title>
                                    <div className="h5 mb-0">
                                        <i className="fad fa-school text-primary icon"></i>
                                        {schoolModalPurpose === "teacherType" ? (
                                            t("Pages.UsersControl.action.changeTypeToSchoolTeacher")
                                        ) : (
                                            <>
                                                {t("Pages.UsersControl.action.changeSchool")}:
                                                <span className="text-primary mx-1">{compatUser.Name}</span>
                                            </>
                                        )}
                                    </div>
                                </Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <div className="widget widget-category mb-2">
                                    <div className="widget-body">
                                        <div className="form-group w-100">
                                            <AsyncSelect
                                                cacheOptions
                                                isClearable={true}
                                                name="SchoolsId"
                                                placeholder={t('Pages.UsersControl.advancedSearch.School')}
                                                isRtl={true}
                                                isSearchable={true}
                                                defaultOptions
                                                loadOptions={promiseSchoolOptions}
                                                ref={schoolSelectRef}
                                                theme={theme => ({
                                                    ...theme,
                                                    borderRadius: 0,
                                                    colors: {
                                                        ...theme.colors,
                                                        primary25: '#92278f',
                                                        primary: '#92278f',
                                                    },
                                                })}
                                            />
                                        </div>
                                        <div className="form-group w-100 text-center">
                                            <button className="custom-button" onClick={handleSchoolSave}>
                                                <span>
                                                    <i className="icon fal fa-save"></i> {t('Pages.UsersControl.notes.Send')}
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </Modal.Body>
                        </Modal>
                    )}

                    {showMove && (
                        <Modal size="md" centered backdrop="static" show={showMove} onHide={() => setShowMove(false)}>
                            <Modal.Header closeButton>
                                <Modal.Title>
                                    <div className="h5 mb-0">
                                        <i className="fad fa-exchange text-primary icon"></i>
                                        {t("Pages.StudentInClassRoomsControl.action.move")}
                                    </div>
                                </Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <div className="widget widget-category mb-2">
                                    <div className="widget-body">
                                        <div className="form-group w-100">
                                            <AsyncSelect
                                                cacheOptions
                                                isClearable={true}
                                                name="ClassRoomId"
                                                placeholder={t('Pages.ClassRoomsControl.title')}
                                                isRtl={true}
                                                isSearchable={true}
                                                defaultOptions
                                                loadOptions={promiseClassRoomOptions}
                                                ref={classRoomSelectRef}
                                                theme={theme => ({
                                                    ...theme,
                                                    borderRadius: 0,
                                                    colors: {
                                                        ...theme.colors,
                                                        primary25: '#92278f',
                                                        primary: '#92278f',
                                                    },
                                                })}
                                            />
                                        </div>
                                        <div className="form-group w-100 text-center">
                                            <button className="custom-button" onClick={handleClassRoomSave}>
                                                <span>
                                                    <i className="icon fal fa-exchange"></i> {t('Pages.StudentInClassRoomsControl.action.move_action')}
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </Modal.Body>
                        </Modal>
                    )}
                </>
            )}
        </>
    );
};

export default SMSModals;
